/**
 * OAuth Integration Handler
 * 
 * Handles OAuth flows for integrations that support it.
 * Provides "Sign in with [Service]" buttons for easy connection.
 */

export interface OAuthProvider {
  id: string;
  name: string;
  authUrl: string;
  tokenUrl: string;
  clientId: string;
  scopes: string[];
  redirectUri: string;
}

export interface OAuthConfig {
  clientId: string;
  clientSecret: string;
  redirectUri: string;
  scopes: string[];
}

// OAuth providers that support "Sign in with X" flow
// Comprehensive list of all OAuth-capable providers
export const OAUTH_PROVIDERS: Partial<Record<string, OAuthProvider>> = {
  // Major Platforms
  google: {
    id: "google",
    name: "Google",
    authUrl: "https://accounts.google.com/o/oauth2/v2/auth",
    tokenUrl: "https://oauth2.googleapis.com/token",
    clientId: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || "",
    scopes: [
      "https://www.googleapis.com/auth/gmail.readonly",
      "https://www.googleapis.com/auth/calendar.readonly",
      "https://www.googleapis.com/auth/drive.readonly",
      "https://www.googleapis.com/auth/contacts.readonly",
    ],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/google`,
  },
  microsoft: {
    id: "microsoft",
    name: "Microsoft",
    authUrl: "https://login.microsoftonline.com/common/oauth2/v2.0/authorize",
    tokenUrl: "https://login.microsoftonline.com/common/oauth2/v2.0/token",
    clientId: process.env.NEXT_PUBLIC_MICROSOFT_CLIENT_ID || "",
    scopes: [
      "https://graph.microsoft.com/User.Read",
      "https://graph.microsoft.com/Mail.Read",
      "https://graph.microsoft.com/Calendars.Read",
    ],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/microsoft`,
  },
  apple: {
    id: "apple",
    name: "Apple",
    authUrl: "https://appleid.apple.com/auth/authorize",
    tokenUrl: "https://appleid.apple.com/auth/token",
    clientId: process.env.NEXT_PUBLIC_APPLE_CLIENT_ID || "",
    scopes: ["name", "email"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/apple`,
  },
  meta: {
    id: "meta",
    name: "Meta (Facebook/Instagram)",
    authUrl: "https://www.facebook.com/v18.0/dialog/oauth",
    tokenUrl: "https://graph.facebook.com/v18.0/oauth/access_token",
    clientId: process.env.NEXT_PUBLIC_META_CLIENT_ID || "",
    scopes: ["email", "public_profile", "pages_read_engagement"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/meta`,
  },
  
  // Communication & Collaboration
  slack: {
    id: "slack",
    name: "Slack",
    authUrl: "https://slack.com/oauth/v2/authorize",
    tokenUrl: "https://slack.com/api/oauth.v2.access",
    clientId: process.env.NEXT_PUBLIC_SLACK_CLIENT_ID || "",
    scopes: ["channels:read", "chat:write", "files:read", "users:read"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/slack`,
  },
  discord: {
    id: "discord",
    name: "Discord",
    authUrl: "https://discord.com/api/oauth2/authorize",
    tokenUrl: "https://discord.com/api/oauth2/token",
    clientId: process.env.NEXT_PUBLIC_DISCORD_CLIENT_ID || "",
    scopes: ["bot", "guilds", "messages.read"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/discord`,
  },
  zoom: {
    id: "zoom",
    name: "Zoom",
    authUrl: "https://zoom.us/oauth/authorize",
    tokenUrl: "https://zoom.us/oauth/token",
    clientId: process.env.NEXT_PUBLIC_ZOOM_CLIENT_ID || "",
    scopes: ["meeting:write", "user:read"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/zoom`,
  },
  
  // Media & Entertainment
  spotify: {
    id: "spotify",
    name: "Spotify",
    authUrl: "https://accounts.spotify.com/authorize",
    tokenUrl: "https://accounts.spotify.com/api/token",
    clientId: process.env.NEXT_PUBLIC_SPOTIFY_CLIENT_ID || "",
    scopes: ["user-read-private", "user-read-email", "playlist-read-private", "playlist-modify-public"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/spotify`,
  },
  soundcloud: {
    id: "soundcloud",
    name: "SoundCloud",
    authUrl: "https://soundcloud.com/connect",
    tokenUrl: "https://api.soundcloud.com/oauth2/token",
    clientId: process.env.NEXT_PUBLIC_SOUNDCLOUD_CLIENT_ID || "",
    scopes: ["non-expiring"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/soundcloud`,
  },
  twitch: {
    id: "twitch",
    name: "Twitch",
    authUrl: "https://id.twitch.tv/oauth2/authorize",
    tokenUrl: "https://id.twitch.tv/oauth2/token",
    clientId: process.env.NEXT_PUBLIC_TWITCH_CLIENT_ID || "",
    scopes: ["user:read:email", "channel:read:stream_key"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/twitch`,
  },
  vimeo: {
    id: "vimeo",
    name: "Vimeo",
    authUrl: "https://api.vimeo.com/oauth/authorize",
    tokenUrl: "https://api.vimeo.com/oauth/access_token",
    clientId: process.env.NEXT_PUBLIC_VIMEO_CLIENT_ID || "",
    scopes: ["public", "private", "upload"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/vimeo`,
  },
  
  // Design & Creative
  canva: {
    id: "canva",
    name: "Canva",
    authUrl: "https://www.canva.com/api/oauth/authorize",
    tokenUrl: "https://api.canva.com/rest/v1/oauth/token",
    clientId: process.env.NEXT_PUBLIC_CANVA_CLIENT_ID || "",
    scopes: ["design:read", "design:write", "user:read"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/canva`,
  },
  
  // Developer Tools
  github: {
    id: "github",
    name: "GitHub",
    authUrl: "https://github.com/login/oauth/authorize",
    tokenUrl: "https://github.com/login/oauth/access_token",
    clientId: process.env.NEXT_PUBLIC_GITHUB_CLIENT_ID || "",
    scopes: ["repo", "read:user", "read:org"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/github`,
  },
  gitlab: {
    id: "gitlab",
    name: "GitLab",
    authUrl: "https://gitlab.com/oauth/authorize",
    tokenUrl: "https://gitlab.com/oauth/token",
    clientId: process.env.NEXT_PUBLIC_GITLAB_CLIENT_ID || "",
    scopes: ["api", "read_api", "write_api"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/gitlab`,
  },
  bitbucket: {
    id: "bitbucket",
    name: "Bitbucket",
    authUrl: "https://bitbucket.org/site/oauth2/authorize",
    tokenUrl: "https://bitbucket.org/site/oauth2/access_token",
    clientId: process.env.NEXT_PUBLIC_BITBUCKET_CLIENT_ID || "",
    scopes: ["repository:read", "repository:write"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/bitbucket`,
  },
  vercel: {
    id: "vercel",
    name: "Vercel",
    authUrl: "https://vercel.com/integrations/authorize",
    tokenUrl: "https://api.vercel.com/v2/oauth/access_token",
    clientId: process.env.NEXT_PUBLIC_VERCEL_CLIENT_ID || "",
    scopes: ["read", "write"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/vercel`,
  },
  netlify: {
    id: "netlify",
    name: "Netlify",
    authUrl: "https://app.netlify.com/authorize",
    tokenUrl: "https://api.netlify.com/oauth/token",
    clientId: process.env.NEXT_PUBLIC_NETLIFY_CLIENT_ID || "",
    scopes: ["read", "write"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/netlify`,
  },
  
  // Productivity
  notion: {
    id: "notion",
    name: "Notion",
    authUrl: "https://api.notion.com/v1/oauth/authorize",
    tokenUrl: "https://api.notion.com/v1/oauth/token",
    clientId: process.env.NEXT_PUBLIC_NOTION_CLIENT_ID || "",
    scopes: ["read", "update"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/notion`,
  },
  airtable: {
    id: "airtable",
    name: "Airtable",
    authUrl: "https://airtable.com/oauth2/v1/authorize",
    tokenUrl: "https://airtable.com/oauth2/v1/token",
    clientId: process.env.NEXT_PUBLIC_AIRTABLE_CLIENT_ID || "",
    scopes: ["data.records:read", "data.records:write"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/airtable`,
  },
  trello: {
    id: "trello",
    name: "Trello",
    authUrl: "https://trello.com/1/OAuthAuthorizeToken",
    tokenUrl: "https://trello.com/1/OAuthGetAccessToken",
    clientId: process.env.NEXT_PUBLIC_TRELLO_CLIENT_ID || "",
    scopes: ["read", "write"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/trello`,
  },
  asana: {
    id: "asana",
    name: "Asana",
    authUrl: "https://app.asana.com/-/oauth_authorize",
    tokenUrl: "https://app.asana.com/-/oauth_token",
    clientId: process.env.NEXT_PUBLIC_ASANA_CLIENT_ID || "",
    scopes: ["default"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/asana`,
  },
  atlassian: {
    id: "atlassian",
    name: "Atlassian (Jira/Confluence)",
    authUrl: "https://auth.atlassian.com/authorize",
    tokenUrl: "https://auth.atlassian.com/oauth/token",
    clientId: process.env.NEXT_PUBLIC_ATLASSIAN_CLIENT_ID || "",
    scopes: ["read:jira-work", "write:jira-work", "read:confluence-content.all"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/atlassian`,
  },
  monday: {
    id: "monday",
    name: "Monday.com",
    authUrl: "https://auth.monday.com/oauth2/authorize",
    tokenUrl: "https://auth.monday.com/oauth2/token",
    clientId: process.env.NEXT_PUBLIC_MONDAY_CLIENT_ID || "",
    scopes: ["boards:read", "boards:write"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/monday`,
  },
  clickup: {
    id: "clickup",
    name: "ClickUp",
    authUrl: "https://app.clickup.com/api",
    tokenUrl: "https://api.clickup.com/api/v2/oauth/token",
    clientId: process.env.NEXT_PUBLIC_CLICKUP_CLIENT_ID || "",
    scopes: ["read", "write"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/clickup`,
  },
  
  // CRM & Sales
  salesforce: {
    id: "salesforce",
    name: "Salesforce",
    authUrl: "https://login.salesforce.com/services/oauth2/authorize",
    tokenUrl: "https://login.salesforce.com/services/oauth2/token",
    clientId: process.env.NEXT_PUBLIC_SALESFORCE_CLIENT_ID || "",
    scopes: ["api", "refresh_token", "offline_access"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/salesforce`,
  },
  hubspot: {
    id: "hubspot",
    name: "HubSpot",
    authUrl: "https://app.hubspot.com/oauth/authorize",
    tokenUrl: "https://api.hubapi.com/oauth/v1/token",
    clientId: process.env.NEXT_PUBLIC_HUBSPOT_CLIENT_ID || "",
    scopes: ["contacts", "content", "files"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/hubspot`,
  },
  zoho: {
    id: "zoho",
    name: "Zoho",
    authUrl: "https://accounts.zoho.com/oauth/v2/auth",
    tokenUrl: "https://accounts.zoho.com/oauth/v2/token",
    clientId: process.env.NEXT_PUBLIC_ZOHO_CLIENT_ID || "",
    scopes: ["ZohoCRM.modules.ALL"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/zoho`,
  },
  pipedrive: {
    id: "pipedrive",
    name: "Pipedrive",
    authUrl: "https://oauth.pipedrive.com/oauth/authorize",
    tokenUrl: "https://oauth.pipedrive.com/oauth/token",
    clientId: process.env.NEXT_PUBLIC_PIPEDRIVE_CLIENT_ID || "",
    scopes: ["read", "write"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/pipedrive`,
  },
  zendesk: {
    id: "zendesk",
    name: "Zendesk",
    authUrl: "https://{subdomain}.zendesk.com/oauth/authorizations/new",
    tokenUrl: "https://{subdomain}.zendesk.com/oauth/tokens",
    clientId: process.env.NEXT_PUBLIC_ZENDESK_CLIENT_ID || "",
    scopes: ["read", "write"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/zendesk`,
  },
  intercom: {
    id: "intercom",
    name: "Intercom",
    authUrl: "https://app.intercom.com/oauth",
    tokenUrl: "https://api.intercom.io/auth/eagle/token",
    clientId: process.env.NEXT_PUBLIC_INTERCOM_CLIENT_ID || "",
    scopes: ["read", "write"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/intercom`,
  },
  
  // E-Commerce
  shopify: {
    id: "shopify",
    name: "Shopify",
    authUrl: "https://{shop}.myshopify.com/admin/oauth/authorize",
    tokenUrl: "https://{shop}.myshopify.com/admin/oauth/access_token",
    clientId: process.env.NEXT_PUBLIC_SHOPIFY_CLIENT_ID || "",
    scopes: ["read_products", "write_products", "read_orders"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/shopify`,
  },
  bigcommerce: {
    id: "bigcommerce",
    name: "BigCommerce",
    authUrl: "https://login.bigcommerce.com/oauth2/authorize",
    tokenUrl: "https://login.bigcommerce.com/oauth2/token",
    clientId: process.env.NEXT_PUBLIC_BIGCOMMERCE_CLIENT_ID || "",
    scopes: ["store_v2_products", "store_v2_orders"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/bigcommerce`,
  },
  square: {
    id: "square",
    name: "Square",
    authUrl: "https://connect.squareup.com/oauth2/authorize",
    tokenUrl: "https://connect.squareup.com/oauth2/token",
    clientId: process.env.NEXT_PUBLIC_SQUARE_CLIENT_ID || "",
    scopes: ["MERCHANT_PROFILE_READ", "PAYMENTS_READ"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/square`,
  },
  etsy: {
    id: "etsy",
    name: "Etsy",
    authUrl: "https://www.etsy.com/oauth/connect",
    tokenUrl: "https://api.etsy.com/v3/public/oauth/token",
    clientId: process.env.NEXT_PUBLIC_ETSY_CLIENT_ID || "",
    scopes: ["listings_r", "listings_w"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/etsy`,
  },
  ebay: {
    id: "ebay",
    name: "eBay",
    authUrl: "https://auth.ebay.com/oauth2/authorize",
    tokenUrl: "https://api.ebay.com/identity/v1/oauth2/token",
    clientId: process.env.NEXT_PUBLIC_EBAY_CLIENT_ID || "",
    scopes: ["https://api.ebay.com/oauth/api_scope"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/ebay`,
  },
  squarespace: {
    id: "squarespace",
    name: "Squarespace",
    authUrl: "https://login.squarespace.com/api/1/login/oauth/provider/authorize",
    tokenUrl: "https://login.squarespace.com/api/1/login/oauth/provider/token",
    clientId: process.env.NEXT_PUBLIC_SQUARESPACE_CLIENT_ID || "",
    scopes: ["read", "write"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/squarespace`,
  },
  wix: {
    id: "wix",
    name: "Wix",
    authUrl: "https://www.wix.com/apps/oauth/authorize",
    tokenUrl: "https://www.wix.com/apps/oauth/access",
    clientId: process.env.NEXT_PUBLIC_WIX_CLIENT_ID || "",
    scopes: ["STORES", "ORDERS"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/wix`,
  },
  
  // Social Media
  twitter: {
    id: "twitter",
    name: "Twitter/X",
    authUrl: "https://twitter.com/i/oauth2/authorize",
    tokenUrl: "https://api.twitter.com/2/oauth2/token",
    clientId: process.env.NEXT_PUBLIC_TWITTER_CLIENT_ID || "",
    scopes: ["tweet.read", "tweet.write", "users.read"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/twitter`,
  },
  linkedin: {
    id: "linkedin",
    name: "LinkedIn",
    authUrl: "https://www.linkedin.com/oauth/v2/authorization",
    tokenUrl: "https://www.linkedin.com/oauth/v2/accessToken",
    clientId: process.env.NEXT_PUBLIC_LINKEDIN_CLIENT_ID || "",
    scopes: ["r_liteprofile", "r_emailaddress", "w_member_social"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/linkedin`,
  },
  reddit: {
    id: "reddit",
    name: "Reddit",
    authUrl: "https://www.reddit.com/api/v1/authorize",
    tokenUrl: "https://www.reddit.com/api/v1/access_token",
    clientId: process.env.NEXT_PUBLIC_REDDIT_CLIENT_ID || "",
    scopes: ["read", "submit"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/reddit`,
  },
  pinterest: {
    id: "pinterest",
    name: "Pinterest",
    authUrl: "https://www.pinterest.com/oauth",
    tokenUrl: "https://api.pinterest.com/v5/oauth/token",
    clientId: process.env.NEXT_PUBLIC_PINTEREST_CLIENT_ID || "",
    scopes: ["boards:read", "pins:read", "pins:write"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/pinterest`,
  },
  tiktok: {
    id: "tiktok",
    name: "TikTok",
    authUrl: "https://www.tiktok.com/v2/auth/authorize",
    tokenUrl: "https://open.tiktokapis.com/v2/oauth/token",
    clientId: process.env.NEXT_PUBLIC_TIKTOK_CLIENT_ID || "",
    scopes: ["user.info.basic", "video.upload"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/tiktok`,
  },
  snapchat: {
    id: "snapchat",
    name: "Snapchat",
    authUrl: "https://accounts.snapchat.com/login/oauth2/authorize",
    tokenUrl: "https://accounts.snapchat.com/login/oauth2/access_token",
    clientId: process.env.NEXT_PUBLIC_SNAPCHAT_CLIENT_ID || "",
    scopes: ["snapchat-marketing-api"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/snapchat`,
  },
  
  // Payments & Finance
  paypal: {
    id: "paypal",
    name: "PayPal",
    authUrl: "https://www.paypal.com/connect",
    tokenUrl: "https://api.paypal.com/v1/oauth2/token",
    clientId: process.env.NEXT_PUBLIC_PAYPAL_CLIENT_ID || "",
    scopes: ["openid", "profile", "email"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/paypal`,
  },
  coinbase: {
    id: "coinbase",
    name: "Coinbase",
    authUrl: "https://www.coinbase.com/oauth/authorize",
    tokenUrl: "https://api.coinbase.com/oauth/token",
    clientId: process.env.NEXT_PUBLIC_COINBASE_CLIENT_ID || "",
    scopes: ["wallet:accounts:read", "wallet:transactions:read"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/coinbase`,
  },
  xero: {
    id: "xero",
    name: "Xero",
    authUrl: "https://login.xero.com/identity/connect/authorize",
    tokenUrl: "https://identity.xero.com/connect/token",
    clientId: process.env.NEXT_PUBLIC_XERO_CLIENT_ID || "",
    scopes: ["accounting.transactions", "accounting.contacts"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/xero`,
  },
  quickbooks: {
    id: "quickbooks",
    name: "QuickBooks",
    authUrl: "https://appcenter.intuit.com/connect/oauth2",
    tokenUrl: "https://oauth.platform.intuit.com/oauth2/v1/tokens/bearer",
    clientId: process.env.NEXT_PUBLIC_QUICKBOOKS_CLIENT_ID || "",
    scopes: ["com.intuit.quickbooks.accounting"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/quickbooks`,
  },
  truelayer: {
    id: "truelayer",
    name: "TrueLayer",
    authUrl: "https://auth.truelayer.com",
    tokenUrl: "https://auth.truelayer.com/connect/token",
    clientId: process.env.NEXT_PUBLIC_TRUELAYER_CLIENT_ID || "",
    scopes: ["accounts", "transactions"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/truelayer`,
  },
  
  // Marketing & Email
  mailchimp: {
    id: "mailchimp",
    name: "Mailchimp",
    authUrl: "https://login.mailchimp.com/oauth2/authorize",
    tokenUrl: "https://login.mailchimp.com/oauth2/token",
    clientId: process.env.NEXT_PUBLIC_MAILCHIMP_CLIENT_ID || "",
    scopes: ["audience_read", "audience_write"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/mailchimp`,
  },
  
  // Other
  okta: {
    id: "okta",
    name: "Okta",
    authUrl: "https://{domain}.okta.com/oauth2/v1/authorize",
    tokenUrl: "https://{domain}.okta.com/oauth2/v1/token",
    clientId: process.env.NEXT_PUBLIC_OKTA_CLIENT_ID || "",
    scopes: ["openid", "profile", "email"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/okta`,
  },
  auth0: {
    id: "auth0",
    name: "Auth0",
    authUrl: "https://{domain}.auth0.com/authorize",
    tokenUrl: "https://{domain}.auth0.com/oauth/token",
    clientId: process.env.NEXT_PUBLIC_AUTH0_CLIENT_ID || "",
    scopes: ["openid", "profile", "email"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/auth0`,
  },
  servicenow: {
    id: "servicenow",
    name: "ServiceNow",
    authUrl: "https://{instance}.service-now.com/oauth_do",
    tokenUrl: "https://{instance}.service-now.com/oauth_token.do",
    clientId: process.env.NEXT_PUBLIC_SERVICENOW_CLIENT_ID || "",
    scopes: ["useraccount", "table"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/servicenow`,
  },
  workday: {
    id: "workday",
    name: "Workday",
    authUrl: "https://wd2-impl.workday.com/ccx/oauth2/{tenant}/authorize",
    tokenUrl: "https://wd2-impl.workday.com/ccx/oauth2/{tenant}/token",
    clientId: process.env.NEXT_PUBLIC_WORKDAY_CLIENT_ID || "",
    scopes: ["wd.audit", "wd.worker"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/workday`,
  },
  gusto: {
    id: "gusto",
    name: "Gusto",
    authUrl: "https://api.gusto.com/oauth/authorize",
    tokenUrl: "https://api.gusto.com/oauth/token",
    clientId: process.env.NEXT_PUBLIC_GUSTO_CLIENT_ID || "",
    scopes: ["read", "write"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/gusto`,
  },
  patreon: {
    id: "patreon",
    name: "Patreon",
    authUrl: "https://www.patreon.com/oauth2/authorize",
    tokenUrl: "https://www.patreon.com/api/oauth2/token",
    clientId: process.env.NEXT_PUBLIC_PATREON_CLIENT_ID || "",
    scopes: ["identity", "campaigns"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/patreon`,
  },
  tesla: {
    id: "tesla",
    name: "Tesla",
    authUrl: "https://auth.tesla.com/oauth2/v3/authorize",
    tokenUrl: "https://auth.tesla.com/oauth2/v3/token",
    clientId: process.env.NEXT_PUBLIC_TESLA_CLIENT_ID || "",
    scopes: ["openid", "offline_access", "user_data", "vehicle_device_data"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/tesla`,
  },
  uber: {
    id: "uber",
    name: "Uber",
    authUrl: "https://login.uber.com/oauth/v2/authorize",
    tokenUrl: "https://login.uber.com/oauth/v2/token",
    clientId: process.env.NEXT_PUBLIC_UBER_CLIENT_ID || "",
    scopes: ["profile", "request"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/uber`,
  },
  tradier: {
    id: "tradier",
    name: "Tradier",
    authUrl: "https://api.tradier.com/v1/oauth/authorize",
    tokenUrl: "https://api.tradier.com/v1/oauth/accesstoken",
    clientId: process.env.NEXT_PUBLIC_TRADIER_CLIENT_ID || "",
    scopes: ["read", "trade"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/tradier`,
  },
  amadeus: {
    id: "amadeus",
    name: "Amadeus",
    authUrl: "https://test.api.amadeus.com/v1/security/oauth2/authorize",
    tokenUrl: "https://test.api.amadeus.com/v1/security/oauth2/token",
    clientId: process.env.NEXT_PUBLIC_AMADEUS_CLIENT_ID || "",
    scopes: ["read", "write"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/amadeus`,
  },
  udemy: {
    id: "udemy",
    name: "Udemy",
    authUrl: "https://www.udemy.com/api-2.0/oauth2/authorize",
    tokenUrl: "https://www.udemy.com/api-2.0/oauth2/token",
    clientId: process.env.NEXT_PUBLIC_UDEMY_CLIENT_ID || "",
    scopes: ["read", "write"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/udemy`,
  },
  trustpilot: {
    id: "trustpilot",
    name: "Trustpilot",
    authUrl: "https://authenticate.trustpilot.com/oauth/authorize",
    tokenUrl: "https://authenticate.trustpilot.com/oauth/token",
    clientId: process.env.NEXT_PUBLIC_TRUSTPILOT_CLIENT_ID || "",
    scopes: ["read", "write"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/trustpilot`,
  },
  deputy: {
    id: "deputy",
    name: "Deputy",
    authUrl: "https://once.deputy.com/my/oauth/login",
    tokenUrl: "https://once.deputy.com/my/oauth/access_token",
    clientId: process.env.NEXT_PUBLIC_DEPUTY_CLIENT_ID || "",
    scopes: ["longlife"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/deputy`,
  },
  adp: {
    id: "adp",
    name: "ADP",
    authUrl: "https://accounts.adp.com/auth/oauth/v2/authorize",
    tokenUrl: "https://accounts.adp.com/auth/oauth/v2/token",
    clientId: process.env.NEXT_PUBLIC_ADP_CLIENT_ID || "",
    scopes: ["api"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/adp`,
  },
  philips_hue: {
    id: "hue",
    name: "Philips Hue",
    authUrl: "https://api.meethue.com/oauth2/auth",
    tokenUrl: "https://api.meethue.com/oauth2/token",
    clientId: process.env.NEXT_PUBLIC_HUE_CLIENT_ID || "",
    scopes: ["read", "write"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/hue`,
  },
  smartthings: {
    id: "smartthings",
    name: "SmartThings",
    authUrl: "https://account.smartthings.com/oauth/authorize",
    tokenUrl: "https://account.smartthings.com/oauth/token",
    clientId: process.env.NEXT_PUBLIC_SMARTTHINGS_CLIENT_ID || "",
    scopes: ["r:devices:*", "x:devices:*"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/smartthings`,
  },
  geotab: {
    id: "geotab",
    name: "Geotab",
    authUrl: "https://my.geotab.com/apiv1/oauth2/authorize",
    tokenUrl: "https://my.geotab.com/apiv1/oauth2/token",
    clientId: process.env.NEXT_PUBLIC_GEOTAB_CLIENT_ID || "",
    scopes: ["read", "write"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/geotab`,
  },
  garmin: {
    id: "garmin",
    name: "Garmin",
    authUrl: "https://connect.garmin.com/oauthConfirm",
    tokenUrl: "https://connectapi.garmin.com/oauth-service/oauth/exchange/user/2.0",
    clientId: process.env.NEXT_PUBLIC_GARMIN_CLIENT_ID || "",
    scopes: ["read", "write"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/garmin`,
  },
  oura: {
    id: "oura",
    name: "Oura",
    authUrl: "https://cloud.ouraring.com/oauth/authorize",
    tokenUrl: "https://api.ouraring.com/oauth/token",
    clientId: process.env.NEXT_PUBLIC_OURA_CLIENT_ID || "",
    scopes: ["personal", "daily"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/oura`,
  },
  whoop: {
    id: "whoop",
    name: "Whoop",
    authUrl: "https://api.prod.whoop.com/oauth/oauth2/auth",
    tokenUrl: "https://api.prod.whoop.com/oauth/oauth2/token",
    clientId: process.env.NEXT_PUBLIC_WHOOP_CLIENT_ID || "",
    scopes: ["read:recovery", "read:workout"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/whoop`,
  },
  epic: {
    id: "epic",
    name: "Epic",
    authUrl: "https://fhir.epic.com/interconnect-fhir-oauth/oauth2/authorize",
    tokenUrl: "https://fhir.epic.com/interconnect-fhir-oauth/oauth2/token",
    clientId: process.env.NEXT_PUBLIC_EPIC_CLIENT_ID || "",
    scopes: ["system/*.read"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/epic`,
  },
  cerner: {
    id: "cerner",
    name: "Cerner",
    authUrl: "https://authorization.cerner.com/tenants/{tenant}/protocols/oauth2/profiles/smart-v1/personas/provider/authorize",
    tokenUrl: "https://authorization.cerner.com/tenants/{tenant}/protocols/oauth2/profiles/smart-v1/token",
    clientId: process.env.NEXT_PUBLIC_CERNER_CLIENT_ID || "",
    scopes: ["system/*.read"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/cerner`,
  },
  netsuite: {
    id: "netsuite",
    name: "NetSuite",
    authUrl: "https://{account}.app.netsuite.com/app/login/oauth2/auth.nl",
    tokenUrl: "https://{account}.app.netsuite.com/app/login/oauth2/token.nl",
    clientId: process.env.NEXT_PUBLIC_NETSUITE_CLIENT_ID || "",
    scopes: ["restlets", "rest_webservices"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/netsuite`,
  },
  magento: {
    id: "magento",
    name: "Magento",
    authUrl: "https://{domain}/oauth/authorize",
    tokenUrl: "https://{domain}/oauth/token",
    clientId: process.env.NEXT_PUBLIC_MAGENTO_CLIENT_ID || "",
    scopes: ["all"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/magento`,
  },
  ping: {
    id: "ping",
    name: "Ping Identity",
    authUrl: "https://{domain}.pingone.com/{env}/as/authorize",
    tokenUrl: "https://{domain}.pingone.com/{env}/as/token",
    clientId: process.env.NEXT_PUBLIC_PING_CLIENT_ID || "",
    scopes: ["openid", "profile", "email"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/ping`,
  },
  tink: {
    id: "tink",
    name: "Tink",
    authUrl: "https://www.tink.com/oauth/authorize",
    tokenUrl: "https://api.tink.com/api/v1/oauth/token",
    clientId: process.env.NEXT_PUBLIC_TINK_CLIENT_ID || "",
    scopes: ["accounts:read", "transactions:read"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/tink`,
  },
  myob: {
    id: "myob",
    name: "MYOB",
    authUrl: "https://secure.myob.com/oauth2/account/authorize",
    tokenUrl: "https://secure.myob.com/oauth2/v1/authorize",
    clientId: process.env.NEXT_PUBLIC_MYOB_CLIENT_ID || "",
    scopes: ["CompanyFile"],
    redirectUri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/oauth/callback/myob`,
  },
};

/**
 * Initiate OAuth flow
 */
export function initiateOAuth(providerId: string, state?: string): void {
  const provider = OAUTH_PROVIDERS[providerId];
  if (!provider) {
    throw new Error(`OAuth provider ${providerId} not found`);
  }

  const params = new URLSearchParams({
    client_id: provider.clientId,
    redirect_uri: provider.redirectUri,
    response_type: "code",
    scope: provider.scopes.join(" "),
    ...(state && { state }),
  });

  const authUrl = `${provider.authUrl}?${params.toString()}`;
  window.location.href = authUrl;
}

/**
 * Check if integration supports OAuth
 */
export function supportsOAuth(integrationType: string): boolean {
  // Import mapping dynamically to avoid circular dependencies
  const { INTEGRATION_AUTH_MAP } = require("./oauth-mapping");
  const config = INTEGRATION_AUTH_MAP[integrationType];
  
  if (!config) return false;
  
  return config.supportsOAuth || config.method === "oauth" || config.method === "both";
}

/**
 * Get OAuth provider for integration
 */
export function getOAuthProvider(integrationType: string): OAuthProvider | null {
  // Import mapping dynamically to avoid circular dependencies
  const { INTEGRATION_AUTH_MAP } = require("./oauth-mapping");
  const config = INTEGRATION_AUTH_MAP[integrationType];
  
  if (!config || !config.oauthProvider) return null;
  
  return OAUTH_PROVIDERS[config.oauthProvider] || null;
}

/**
 * Get authentication config for integration
 */
export function getAuthConfig(integrationType: string) {
  const { INTEGRATION_AUTH_MAP } = require("./oauth-mapping");
  return INTEGRATION_AUTH_MAP[integrationType] || null;
}

